import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, TextField, Typography } from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { useField, useFormikContext } from 'formik';
import { format } from 'date-fns';
import { showToast } from '@klpkonsern/ui';

import { Block } from '@klp/design';
import { formatDate, parseSafeDate } from '@klp/utils/dateTimeFormatter';
import type { FormField } from '../interfaces';
import {
    useGetProsjektByggListQuery,
    useLazyGetProjectBudgetInfoQuery,
    useLazyGetProjectBudgetsQuery,
} from '../services/rtkApi';
import { getLocalizedLabel, handleBuildingOptionByValue } from '../utils';

import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';

export const BudsjettnummerSelector = ({
    field,
    isReadOnly,
    isRequired,
}: {
    field?: FormField;
    isReadOnly?: boolean;
    isRequired?: boolean;
}) => {
    const { t } = useTranslation();
    const classes = getFormFieldStyles();
    const [formikField, meta] = useField(field?.id as string);
    const { setFieldValue, values } = useFormikContext();
    const [budgetnumberValue, setBudgetNumberValue] = useState<string>(
        formikField.value || '',
    );
    const [resetField, setResetField] = useState(false);
    const [fetchedFirmaId, setFetchedFirmaId] = useState('');
    const [getProjectBudgets, { data: projectBudgets, isLoading, isFetching }] =
        useLazyGetProjectBudgetsQuery();
    const [getProjectBudgetInfo, { data: projectBudgetInfo }] =
        useLazyGetProjectBudgetInfoQuery();
    const { data: byggData } = useGetProsjektByggListQuery();

    useEffect(() => {
        if (isReadOnly || !values?.['eiendomsnavn']) return;

        const bygg = handleBuildingOptionByValue(
            values['eiendomsnavn'],
            byggData,
        );

        if (
            bygg?.firmaId &&
            bygg?.firmaId !== '' &&
            bygg?.firmaId !== fetchedFirmaId
        ) {
            const fromDate = new Date(new Date().getFullYear(), 0, 1);
            getProjectBudgets({
                firmaId: bygg.firmaId,
                fromDate: format(fromDate, 'yyyy-MM-dd'),
            });
            setFetchedFirmaId(bygg.firmaId);
        }
    }, [values, byggData]);

    useEffect(() => {
        if (
            !isReadOnly &&
            projectBudgets?.includes(budgetnumberValue) &&
            budgetnumberValue !== projectBudgetInfo?.budsjettnummer
        ) {
            getProjectBudgetInfo({ budsjettnummer: budgetnumberValue })
                .unwrap()
                .catch((e) => {
                    if (
                        e?.data?.includes(
                            'Could not find any budgets for budget number',
                        )
                    ) {
                        return;
                    }
                    showToast({
                        body: t('prosjekt.successfulOperation'),
                        type: 'success',
                    });
                });
        }
    }, [budgetnumberValue, projectBudgets]);

    const handleChangeValue = (value?: string | null) => {
        setBudgetNumberValue(value || '');
        setFieldValue(field?.id as string, value || '');
    };

    return (
        <>
            <Autocomplete
                id="budsjettnummer"
                freeSolo={true}
                selectOnFocus={true}
                clearOnBlur={true}
                handleHomeEndKeys={true}
                disabled={isReadOnly}
                options={projectBudgets || []}
                value={budgetnumberValue}
                onChange={(event, value) => {
                    handleChangeValue(value);
                }}
                onInputChange={(event, value, reason) => {
                    handleChangeValue(value);

                    if (reason === 'clear') {
                        setResetField(true);
                    }
                }}
                loading={isLoading || isFetching}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        {...formikField}
                        key={resetField ? 'reset' : 'normal'}
                        fullWidth={true}
                        className={classNames({
                            [classes.editableField]: !isReadOnly,
                            [classes.readOnlyField]: isReadOnly,
                        })}
                        required={isRequired}
                        variant="outlined"
                        disabled={!!isReadOnly}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isLoading || isFetching ? (
                                        <CircularProgress color="inherit" />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        label={getLocalizedLabel(
                            `project.formFields.${field?.id}`,
                            field?.label,
                            field?.id,
                        )}
                        helperText={meta.touched ? meta.error : ''}
                        error={meta.touched && Boolean(meta.error)}
                    />
                )}
            />
            {!isReadOnly &&
                projectBudgetInfo &&
                budgetnumberValue &&
                budgetnumberValue === projectBudgetInfo.budsjettnummer && (
                    <Block marginTop="tiny">
                        <Alert severity="info">
                            <Typography style={{ fontWeight: 'bold' }}>
                                {t('prosjekt.budsjettnummer.title', {
                                    number: projectBudgetInfo.budsjettnummer,
                                })}
                            </Typography>
                            <Typography variant="body1">
                                {t('prosjekt.budsjettnummer.period', {
                                    from: formatDate(
                                        parseSafeDate(projectBudgetInfo.from),
                                    ),
                                    to: formatDate(
                                        parseSafeDate(projectBudgetInfo.to),
                                    ),
                                })}
                            </Typography>
                            <Typography variant="body1">
                                {t('prosjekt.budsjettnummer.budget', {
                                    exMva: projectBudgetInfo.budgetExMva.toLocaleString(
                                        'nb-NO',
                                    ),
                                    mva: projectBudgetInfo.mva.toLocaleString(
                                        'nb-NO',
                                    ),
                                    total: projectBudgetInfo.budgetTotal.toLocaleString(
                                        'nb-NO',
                                    ),
                                })}
                            </Typography>
                        </Alert>
                    </Block>
                )}
        </>
    );
};
